import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../../redux/storeProductSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../../redux/storeCartSlice";
import { fetchAllCommercePartners } from "../../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../../redux/storeProductRatingSlice";
import { logout } from "../../../../redux/authSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../../redux/notificationsSlice";
import { CurrencyContext } from "../../../../GLOBAL/CurrencyContext";
import appLogo from "../../../../assets/app/logo.png";

/* ⭐ CATEGORY SOURCE — the same collection StoreProducts saves with.
   This page is the Electronics department and nothing else: the key
   rail toggles Electronics' own categories, and everything below
   drills category → subCategory → productType → product. */
import {
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, X, ChevronRight, ChevronDown, Plus, Minus, Check,
  Heart, User, ShoppingCart, MapPin, Globe, AlertTriangle, LogOut,
  Cpu, Zap, LayoutGrid, Home, Package, Star, ShoppingBag,
  ShoppingBasket, Sliders, CircleDot,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   SCOPE
═══════════════════════════════════════════════════════════ */
const ELECTRONICS_DEPT_ID = "06";

/* ═══════════════════════════════════════════════════════════
   DESIGN — a lab bench, not a boutique.

   Heartlas cream and gold, laid out like a spec sheet: paper
   panels on a faint blueprint grid, hairline tan borders, mono
   numerals for anything measurable. The palette is the house
   palette, but the page still carries no accent of its own —
   every product-level accent is the seller's brand colour, so a
   wall of products reads as a wall of brands, not of theme.
═══════════════════════════════════════════════════════════ */
const E = {
  void:     "#FBF6EE",   // page
  voidSoft: "#FFFDF9",   // sheets & modals
  panel:    "#FFFFFF",   // cards
  panel2:   "#FFF9EF",
  raise:    "#FCEFD4",
  line:     "#EEDFC4",
  lineSoft: "#F3E6CE",
  ink:      "#1B0E06",
  inkSoft:  "#4A3627",
  muted:    "#7A6653",
  faint:    "#A6947F",
  ok:       "#3F8A4C",
  warn:     "#D4900F",
  danger:   "#C8202E",
};

/* page chrome — the scanline, the pressed key, the cart badge.
   Product-level accents still come from the seller's own colour. */
const ATLAS_RED = "#C8202E";
const KEY_EDGE = "#E0CDA9";   // the tan lip under an unpressed keycap

/* the seller's colour is the only accent on the page */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || ATLAS_RED;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE HELPERS — ancestry always resolved by walking
   parentId, never by string prefixes (PT-… / PRD-… break those).
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | number | null): string[] => {
  if (id === null || id === undefined || id === "") return [];
  const key = String(id);
  const hit = chainCache.get(key);
  if (hit) return hit;
  const chain: string[] = [];
  let cur = CAT_MAP.get(key);
  while (cur) {
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(key, chain);
  return chain;
};

const catName = (id?: string | number | null) =>
  id === null || id === undefined ? "" : CAT_MAP.get(String(id))?.name ?? "";

/** Path with the department stripped — everything here is Electronics. */
const catPathLabel = (id?: string | number | null) =>
  chainIdsOf(id).filter((cid) => cid !== ELECTRONICS_DEPT_ID).map(catName).join(" / ");

const isElectronics = (id?: string | number | null) =>
  chainIdsOf(id).includes(ELECTRONICS_DEPT_ID);

/** Electronics' own categories — the key rail's caps. */
const ELECTRONICS_CATEGORIES: CategoryItem[] = childrenOf(ELECTRONICS_DEPT_ID);

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — the same bridge StoreProducts uses. On electronics
   these are the spec sheet, so they get first-class billing on the
   card rather than being buried in the modal.
═══════════════════════════════════════════════════════════ */
const toAttributeCategoryId = (id?: string | number | null): string => {
  if (id === null || id === undefined || id === "") return "";
  const chain = chainIdsOf(id).map((cid) => CAT_MAP.get(cid)!).filter(Boolean);
  const anchor = [...chain].reverse().find((c) => c.level === "category" || c.level === "department");
  return anchor ? `0${anchor.id}` : "";
};

const attrCache = new Map<string, any[]>();
const resolveAttributes = (categoryId?: string | number | null): any[] => {
  if (categoryId === null || categoryId === undefined || categoryId === "") return [];
  const key = String(categoryId);
  const hit = attrCache.get(key);
  if (hit) return hit;

  const chain = [...chainIdsOf(categoryId)].reverse();
  const keys = [toAttributeCategoryId(categoryId), ...chain.flatMap((c) => [`0${c}`, c])].filter(Boolean);

  let found: any[] = [];
  for (const k of keys) {
    try {
      const defs = attributesForCategory(k as any);
      if (defs && defs.length) { found = defs; break; }
    } catch { /* unknown key — keep walking up */ }
  }
  attrCache.set(key, found);
  return found;
};

const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

const isEmptyValue = (v: any) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/** Attributes whose options make them a buyable variant (Colour, Capacity…). */
const isVariantAttr = (attr: any) =>
  (attr?.inputType === "select" || !attr?.inputType) &&
  Array.isArray(attr?.options) && attr.options.length > 0;

const variantAttrsForItem = (item: any) => resolveAttributes(item?.categoryId).filter(isVariantAttr);
const hasVariants = (item: any) => variantAttrsForItem(item).length > 0;

interface Spec { name: string; value: string }
/** The spec sheet: only what the seller actually filled in. */
const specsOf = (item: any): Spec[] => {
  const saved = item?.attributes ?? {};
  return resolveAttributes(item?.categoryId)
    .filter((def: any) => !isEmptyValue(saved[String(def.id)]))
    .map((def: any) => ({ name: def.name, value: resolveAttrValue(def, saved[String(def.id)]) }))
    .filter((s) => s.value);
};

/** Saved colour swatches, if the seller pinned one. */
interface Swatch { id: string; name: string; hex?: string }
const savedColorsOf = (item: any): Swatch[] => {
  const saved = item?.attributes ?? {};
  const out: Swatch[] = [];
  resolveAttributes(item?.categoryId)
    .filter((def: any) => def.inputType === "color" && !isEmptyValue(saved[String(def.id)]))
    .forEach((def: any) => {
      const raw = saved[String(def.id)];
      (Array.isArray(raw) ? raw : [raw]).forEach((optId: any) => {
        const opt = def.options?.find((o: any) => String(o.id) === String(optId));
        if (opt) out.push({ id: String(opt.id), name: opt.name || opt.standard || String(opt.id), hex: opt.hex });
      });
    });
  return out;
};

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";

const isOutOfStock = (item: any) => item?.available === false || (item?.stock ?? 0) <= 0;

/* ═══════════════════════════════════════════════════════════
   LOCATION TIERS
═══════════════════════════════════════════════════════════ */
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null =>
  country ? COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null : null;
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=JetBrains+Mono:wght@400;500;700&family=Inter:wght@400;500;600;700&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-thumb { background: ${E.raise}; border-radius: 99px; }
input, button, select { font-family: inherit; }
.es :focus-visible { outline: 2px solid ${ATLAS_RED}; outline-offset: 2px; border-radius: 6px; }

@keyframes esScan { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
@keyframes esIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes esBlink { 0%,100% { opacity: 1; } 50% { opacity: .25; } }
@keyframes esSpin { to { transform: rotate(360deg); } }

/* blueprint grid under everything */
.es {
  min-height: 100vh; background: ${E.void}; color: ${E.ink};
  font-family: 'Inter', sans-serif; padding-bottom: 96px; position: relative;
  background-image:
    linear-gradient(${E.lineSoft} 1px, transparent 1px),
    linear-gradient(90deg, ${E.lineSoft} 1px, transparent 1px);
  background-size: 46px 46px;
}
.es-mono { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
.es-disp { font-family: 'Space Grotesk', 'Inter', sans-serif; font-weight: 700; letter-spacing: -.02em; }

/* ── power rail ── */
.es-rail {
  position: sticky; top: 0; z-index: 200;
  background: rgba(251,246,238,.88); backdrop-filter: blur(14px);
  border-bottom: 1px solid ${E.line};
}
.es-scan { height: 1px; overflow: hidden; position: relative; background: ${E.line}; }
.es-scan i { position: absolute; inset: 0; width: 40%;
  background: linear-gradient(90deg, transparent, ${ATLAS_RED}, transparent);
  animation: esScan 3.4s linear infinite; }

.es-hbtn { display:flex; align-items:center; gap:6px; background:${E.panel};
  border:1px solid ${E.line}; color:${E.inkSoft}; font-size:12px; font-weight:600;
  padding:9px 13px; border-radius:10px; cursor:pointer; flex-shrink:0; white-space:nowrap;
  transition: border-color .16s, color .16s, background .16s; }
.es-hbtn:hover { border-color:${E.raise}; color:${E.ink}; background:${E.panel2}; }

.es-search { display:flex; align-items:center; flex:1; min-width:0; background:${E.panel};
  border:1px solid ${E.line}; border-radius:10px; overflow:hidden; transition:border-color .16s; }
.es-search:focus-within { border-color:${ATLAS_RED}; }
.es-search input { flex:1; min-width:0; background:none; border:none; outline:none;
  padding:10px 12px; font-size:13px; color:${E.ink}; }
.es-search input::placeholder { color:${E.faint}; }

/* ── ⌨ THE KEY RAIL — categories as keycaps you press ── */
.es-keys { display:flex; gap:8px; overflow-x:auto; scrollbar-width:none; padding:2px 2px 8px; }
.es-key {
  position: relative; flex-shrink:0; display:flex; align-items:center; gap:8px;
  padding:11px 16px 12px; border-radius:11px; cursor:pointer; white-space:nowrap;
  font-size:12.5px; font-weight:600; color:${E.inkSoft};
  background: linear-gradient(180deg, ${E.panel2} 0%, ${E.panel} 100%);
  border:1px solid ${E.line};
  box-shadow: 0 3px 0 ${KEY_EDGE}, inset 0 1px 0 rgba(255,255,255,.9);
  transition: transform .1s, box-shadow .1s, color .16s, border-color .16s;
}
.es-key:hover { color:${E.ink}; border-color:${E.raise}; }
.es-key:active { transform: translateY(3px); box-shadow: 0 0 0 ${KEY_EDGE}, inset 0 1px 0 rgba(255,255,255,.9); }
.es-key.on {
  color:#fff; transform: translateY(3px);
  box-shadow: 0 0 0 ${KEY_EDGE}, inset 0 2px 8px rgba(27,14,6,.30);
}
.es-key .es-led { width:6px; height:6px; border-radius:50%; background:${E.faint}; flex-shrink:0; }
.es-key.on .es-led { animation: esBlink 1.6s ease-in-out infinite; }

/* ── product card: a spec card ── */
.es-card { background:${E.panel}; border:1px solid ${E.line}; border-radius:14px;
  overflow:hidden; cursor:pointer; display:flex; flex-direction:column;
  animation: esIn .26s ease both; transition: border-color .2s, transform .2s, box-shadow .2s; }
.es-card:hover { transform: translateY(-4px); }
.es-shot { position:relative; width:100%; aspect-ratio:4/3; background:
  radial-gradient(120% 90% at 50% 15%, ${E.panel2} 0%, ${E.panel} 70%);
  display:flex; align-items:center; justify-content:center; overflow:hidden; }
.es-shot img { width:82%; height:82%; object-fit:contain; display:block;
  filter: drop-shadow(0 12px 18px rgba(27,14,6,.16)); transition: transform .3s; }
.es-card:hover .es-shot img { transform: scale(1.05); }

.es-spec { display:flex; align-items:center; gap:5px; font-size:9.5px; font-weight:500;
  color:${E.muted}; background:${E.panel2}; border:1px solid ${E.line};
  border-radius:6px; padding:3px 7px; white-space:nowrap; }
.es-spec b { color:${E.inkSoft}; font-weight:600; }

.es-tag { position:absolute; top:9px; font-size:9.5px; font-weight:700; letter-spacing:.04em;
  padding:3px 8px; border-radius:6px; }

.es-add { width:100%; height:40px; border:1px solid; border-radius:10px; background:transparent;
  display:flex; align-items:center; justify-content:center; gap:7px; cursor:pointer;
  font-size:11.5px; font-weight:700; letter-spacing:.06em; transition: background .16s, color .16s; }

.es-step { display:flex; align-items:center; justify-content:space-between;
  height:40px; border:1px solid; border-radius:10px; padding:0 5px; }
.es-step button { width:28px; height:28px; border-radius:7px; border:none; cursor:pointer;
  display:flex; align-items:center; justify-content:center; }

/* ── panels ── */
.es-panel { background:${E.panel}; border:1px solid ${E.line}; border-radius:14px; }
.es-node { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:9px;
  cursor:pointer; font-size:13px; color:${E.inkSoft}; transition: background .14s, color .14s; }
.es-node:hover { background:${E.panel2}; color:${E.ink}; }
.es-node.on { background:${E.panel2}; color:#fff; }

.es-tile { background:${E.panel2}; border:1px solid ${E.line}; border-radius:11px;
  padding:12px 10px; cursor:pointer; display:flex; flex-direction:column; align-items:center;
  gap:8px; text-align:center; transition: border-color .16s, background .16s, transform .16s; }
.es-tile:hover { border-color:${E.raise}; transform: translateY(-2px); }
.es-tile.on { border-color:${ATLAS_RED}; background:${E.raise}; }

.es-tab { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px;
  font-weight:600; cursor:pointer; color:${E.faint}; padding:4px 10px; background:none; border:none; }
.es-tab.on { color:${E.ink}; }

.es-sheet { background:${E.panel}; border:1px solid ${E.line}; }
.es-nav { display:flex; align-items:center; gap:11px; padding:12px 16px; cursor:pointer;
  font-size:13.5px; font-weight:600; color:${E.inkSoft}; background:none; width:100%;
  text-align:left; border:none; border-bottom:1px solid ${E.line}; }
.es-nav:hover { background:${E.panel2}; color:${E.ink}; }

@media (prefers-reduced-motion: reduce) {
  .es-scan i, .es-key.on .es-led, .es-card { animation: none !important; }
  .es-card, .es-shot img, .es-tile { transition: none; }
}
`;

const S: Record<string, React.CSSProperties> = {
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(232px,1fr))", gap: 16 },
  mobileGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  tabs: {
    position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 62,
    background: "rgba(255,255,255,.95)", backdropFilter: "blur(12px)",
    borderTop: `1px solid ${E.line}`, display: "flex",
    justifyContent: "space-around", alignItems: "center", zIndex: 150,
  },
};

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const ElectronicsStore: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);
  const cur = symbol ?? "€";
  const fx = (n: number) => (n ?? 0) * (rate ?? 1);
  const price = (n: number) => `${cur}${fx(n).toFixed(2)}`;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = authUser?.userId ?? "";

  const filteredListings = useSelector((s: RootState) => s.storeProducts.filteredProducts);
  const cartItems = useSelector((s: RootState) => (s as any).storeCart?.items ?? []);
  const ratingSummaries = useSelector((s: RootState) => (s as any).storeProductRatings?.summaries ?? {});
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  /* the category is the master toggle — this page is one department */
  const [activeCat, setActiveCat] = useState<string>(ELECTRONICS_CATEGORIES[0]?.id ?? ELECTRONICS_DEPT_ID);
  const cat = useMemo(
    () => ELECTRONICS_CATEGORIES.find((c) => c.id === activeCat) ?? ELECTRONICS_CATEGORIES[0],
    [activeCat]
  );
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);

  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [browseOpen, setBrowseOpen] = useState(true);

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [qty, setQty] = useState(1);
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, any>>({});
  const [wished, setWished] = useState<Set<string>>(new Set());

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  /* ── effects ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners() as any); }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null) as any); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId) as any);
    return () => { dispatch(stopUserNotificationsListener() as any); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      dispatch(filterLocalProductsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
              (results: any, status: string) => {
                if (status === "OK" && results?.[0]) {
                  const comps = results[0].address_components;
                  const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                  setUserCity(get("locality") || get("administrative_area_level_2"));
                  const country = get("country"); setUserCountry(country);
                  if (country) dispatch(filterLocalProductsByCountry(country) as any);
                }
              });
          }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          dispatch(filterLocalProductsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName })
        .then((r) => { r.onchange = () => { if (r.state === "granted") detect(); }; })
        .catch(() => {});
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  useEffect(() => {
    if (!detailItem) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [detailItem]);

  /* ── actions ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveSub(null); setActiveDeep(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry) as any);
  };

  const resetAllFilters = () => {
    setActiveSub(null); setActiveDeep(null); setActiveSort("default"); setSearchQuery("");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  };

  const handleCatSelect = (id: string) => {
    setActiveCat(id); setActiveSub(null); setActiveDeep(null); setOpenNodes(new Set());
  };
  const handleSubSelect = (id: string) => { setActiveSub((p) => (p === id ? null : id)); setActiveDeep(null); };
  const handleDeepSelect = (id: string) => setActiveDeep((p) => (p === id ? null : id));

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const openDetail = (item: any) => { setDetailItem(item); setQty(1); setSelectedVariants({}); };
  const closeDetail = () => setDetailItem(null);

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    if (hasVariants(item)) { openDetail(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleDetailContinue = (toCheckout: boolean) => {
    if (!detailItem || isOutOfStock(detailItem)) return;
    const vDefs = variantAttrsForItem(detailItem);
    if (vDefs.some((a: any) => selectedVariants[String(a.id)] == null)) return;

    ensureCartBrandCompat(detailItem.brandId);
    dispatch(addToCart({
      listingId: detailItem.listingId, name: detailItem.name!,
      rate: detailItem.rate!, qty,
      photo: detailItem.photos?.[0],
      hostId: detailItem.hostId, brandId: detailItem.brandId,
      selectedVariants: { ...selectedVariants },
    } as any));
    closeDetail(); setQty(1); setSelectedVariants({});
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToCheckout = () => navigate(`/pay/${userId}`, { state: { items: cartItems } });
  const goToBrand = (id: string) => { if (id && id !== "unknown") navigate(`/brand/${id}`); };
  const handleLogout = async () => { await dispatch(logout() as any); navigate("/AuthSwitch"); };

  /* ═══════════════════════════════════
     FILTERING — always inside Electronics
  ═══════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings || []).filter((l: any) => {
      if (!l.isPublished) return false;
      if (isOutOfStock(l)) return false;
      const chain = chainIdsOf(l.categoryId);
      if (!chain.includes(ELECTRONICS_DEPT_ID)) return false;
      if (!chain.includes(activeCat)) return false;
      if (activeSub && !chain.includes(activeSub)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (q) {
        const specText = specsOf(l).map((s) => `${s.name} ${s.value}`).join(" ");
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${l.vendor ?? ""} ${(l.tags ?? []).join(" ")} ${specText}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "newest") list = [...list].sort((a, b) => (Date.parse(b.createdAt ?? 0) || 0) - (Date.parse(a.createdAt ?? 0) || 0));

    return list;
  }, [filteredListings, activeCat, activeSub, activeDeep, searchQuery, activeSort]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map((bId) => ({ brandId: bId, items: map.get(bId)! }));
    if (locationActive) {
      groups = groups.filter((g) => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const activeFilterName = activeDeep ? catName(activeDeep) : activeSub ? catName(activeSub) : null;
  const catChildren = useMemo(() => childrenOf(activeCat), [activeCat]);

  /* ═══════════════════════════════════
     RAIL  (plain function calls so the search keeps focus)
  ═══════════════════════════════════ */
  const railRow = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: isMobile ? "11px 14px" : "13px 22px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div style={{ width: 30, height: 30, borderRadius: 9, background: E.panel2, border: `1px solid ${E.line}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Cpu size={15} color={ATLAS_RED} />
        </div>
        {!isMobile && <span className="es-disp" style={{ fontSize: 15, color: E.ink }}>Electronics</span>}
      </div>

      {locationDenied ? (
        <button className="es-hbtn" onClick={() => setLocationModalOpen(true)} aria-label="Location off">
          <AlertTriangle size={13} />
        </button>
      ) : !userCountry ? (
        <div className="es-hbtn">
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: ATLAS_RED, animation: "esBlink 1.2s infinite" }} />
          {!isMobile && <span>Locating…</span>}
        </div>
      ) : (
        <button onClick={handleToggleGlobal} className="es-hbtn">
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          {!isMobile && <span>{showGlobal ? "Global" : userCity || userCountry}</span>}
        </button>
      )}

      <div className="es-search">
        <span style={{ padding: "0 4px 0 11px", display: "flex" }}><Search size={14} color={E.faint} /></span>
        <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search model, brand or spec…" aria-label="Search electronics" />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} aria-label="Clear search"
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0 10px", color: E.faint, display: "flex" }}>
            <X size={13} />
          </button>
        )}
      </div>

      <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen((v) => !v))} className="es-hbtn">
        <LayoutGrid size={14} />{!isMobile && <span>Browse</span>}
      </button>

      <button onClick={goToCheckout} className="es-hbtn" style={{ position: "relative" }}>
        <ShoppingCart size={14} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal}` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span className="es-mono" style={{ position: "absolute", top: -6, right: -6, background: ATLAS_RED, color: "#fff", fontSize: 9, fontWeight: 700, minWidth: 17, height: 17, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>

      {!isMobile && (
        <div style={{ position: "relative" }}>
          <button onClick={() => setProfileMenuOpen((v) => !v)} className="es-hbtn" aria-label="Profile"><User size={14} /></button>
          <AnimatePresence>
            {profileMenuOpen && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                className="es-sheet"
                style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 196, borderRadius: 12, overflow: "hidden", zIndex: 400, boxShadow: "0 18px 44px rgba(27,14,6,.16)" }}>
                <button className="es-nav" onClick={() => navigate(`/profile/${userId}`)}><User size={14} /> Profile</button>
                <button className="es-nav" onClick={() => navigate(`/clientdashboard/${userId}`)}><ShoppingBag size={14} /> Orders</button>
                <button className="es-nav" style={{ borderBottom: "none", color: E.danger }} onClick={handleLogout}><LogOut size={14} /> Sign out</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     ⌨ KEY RAIL — Electronics' categories as keycaps
  ═══════════════════════════════════ */
  const keyRail = () => (
    <div className="es-keys">
      {ELECTRONICS_CATEGORIES.map((c) => {
        const on = c.id === activeCat;
        return (
          <button key={c.id} className={`es-key${on ? " on" : ""}`} onClick={() => handleCatSelect(c.id)}
            style={on ? { background: ATLAS_RED, borderColor: ATLAS_RED } : undefined}>
            <span className="es-led" style={on ? { background: "#fff" } : undefined} />
            <span style={{ display: "flex", opacity: on ? 1 : .7 }}>{c.icon}</span>
            {c.name}
          </button>
        );
      })}
    </div>
  );

  const heading = () => (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14, flexWrap: "wrap", padding: "4px 2px 14px" }}>
      <div>
        <div className="es-mono" style={{ fontSize: 10, letterSpacing: ".22em", color: E.faint, textTransform: "uppercase", marginBottom: 5 }}>
          Electronics / {cat?.name ?? "All"}
        </div>
        <h1 className="es-disp" style={{ fontSize: isMobile ? 26 : 36, color: E.ink, lineHeight: 1.05 }}>
          {activeFilterName ?? cat?.name ?? "Electronics"}
        </h1>
      </div>
      <div className="es-mono" style={{ fontSize: 11, color: E.muted, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span><b style={{ color: E.ink }}>{visibleCount}</b> units</span>
        <span style={{ color: E.line }}>│</span>
        <span><b style={{ color: E.ink }}>{productsByBrand.length}</b> sellers</span>
        <span style={{ color: E.line }}>│</span>
        <span>{locationDenied ? "worldwide" : showGlobal ? "global" : userCity ?? userCountry ?? "nearby"}</span>
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     BROWSE TREE
  ═══════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = childrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const picked = activeSub === node.id || activeDeep === node.id;
    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
        <div className={`es-node${picked ? " on" : ""}`}
          onClick={() => {
            if (depth === 0) handleSubSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}>
          <span style={{ display: "flex", color: picked ? ATLAS_RED : E.faint, flexShrink: 0 }}>{node.icon}</span>
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
          {kids.length > 0 && (
            <motion.span animate={{ rotate: isOpen ? 90 : 0 }} style={{ display: "flex", flexShrink: 0 }}>
              <ChevronRight size={13} color={E.faint} />
            </motion.span>
          )}
          {picked && kids.length === 0 && <Check size={13} color={ATLAS_RED} style={{ flexShrink: 0 }} />}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div style={{ borderLeft: `1px solid ${E.line}`, marginLeft: 12, paddingLeft: 4 }}>
                {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const browsePanel = () => (
    <div className="es-panel" style={{ padding: 14 }}>
      <div className="es-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: E.faint, textTransform: "uppercase", marginBottom: 10 }}>
        {cat?.name ?? "Browse"}
      </div>
      {catChildren.length === 0
        ? <p style={{ fontSize: 12, color: E.faint }}>Nothing deeper here yet.</p>
        : catChildren.map((c) => <TreeNode key={c.id} node={c} depth={0} />)}
    </div>
  );

  const mobileBrowseSheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.45)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", background: E.voidSoft, borderTop: `1px solid ${E.line}`, borderRadius: "16px 16px 0 0", zIndex: 401, overflowY: "auto" }}>
            <div style={{ width: 34, height: 3, background: E.raise, borderRadius: 2, margin: "12px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", padding: "12px 18px 10px", borderBottom: `1px solid ${E.line}` }}>
              <span className="es-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: E.muted }}>BROWSE</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", display: "flex" }}>
                <X size={15} color={E.muted} />
              </button>
            </div>
            <div style={{ padding: 16 }}>{keyRail()}<div style={{ height: 12 }} />{browsePanel()}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     SUB-CATEGORY TILES
  ═══════════════════════════════════ */
  const subTiles = () => {
    if (!catChildren.length) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <div onClick={() => setBrowseOpen((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 12 }}>
          <Sliders size={13} color={E.muted} />
          <span className="es-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: E.muted, textTransform: "uppercase" }}>Narrow it down</span>
          <motion.span animate={{ rotate: browseOpen ? 0 : -90 }} style={{ display: "flex" }}>
            <ChevronDown size={15} color={E.faint} />
          </motion.span>
        </div>
        <AnimatePresence initial={false}>
          {browseOpen && (
            <motion.div key={activeCat} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3,1fr)" : "repeat(auto-fill,minmax(128px,1fr))", gap: 10 }}>
                <button className={`es-tile${activeSub === null ? " on" : ""}`}
                  onClick={() => { setActiveSub(null); setActiveDeep(null); }}>
                  <CircleDot size={17} color={activeSub === null ? ATLAS_RED : E.muted} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: activeSub === null ? E.ink : E.inkSoft }}>All</span>
                </button>
                {catChildren.map((node) => {
                  const on = activeSub === node.id;
                  return (
                    <button key={node.id} className={`es-tile${on ? " on" : ""}`} onClick={() => handleSubSelect(node.id)}>
                      <span style={{ display: "flex", fontSize: 17, color: on ? ATLAS_RED : E.muted }}>{node.icon}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: on ? E.ink : E.inkSoft, lineHeight: 1.2 }}>{node.name}</span>
                    </button>
                  );
                })}
              </div>

              {activeSub && childrenOf(activeSub).length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                  {childrenOf(activeSub).map((node) => {
                    const on = activeDeep === node.id;
                    return (
                      <button key={node.id} onClick={() => handleDeepSelect(node.id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 999,
                          border: `1px solid ${on ? ATLAS_RED : E.line}`, background: on ? E.raise : E.panel,
                          color: on ? E.ink : E.inkSoft, fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                        }}>
                        {on && <Check size={11} color={ATLAS_RED} />}
                        {node.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SELLER ROW — underlined in the seller's own colour
  ═══════════════════════════════════ */
  const sellerRow = (brandId: string) => {
    const partner = brandMap.get(brandId);
    const name = partner?.name || "Seller";
    const logo = partner?.image || appLogo;
    const accent = brandAccentColor(partner);
    const city = partner?.address?.city;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${name}`}
          style={{ width: 30, height: 30, borderRadius: 9, overflow: "hidden", flexShrink: 0, border: `1px solid ${E.line}`, background: E.panel, padding: 0, cursor: "pointer" }}>
          <img src={logo} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span onClick={() => goToBrand(brandId)} className="es-disp"
          style={{ fontSize: 14, color: E.ink, cursor: "pointer", borderBottom: `2px solid ${accent}`, paddingBottom: 2 }}>
          {name}
        </span>
        {city && <span className="es-mono" style={{ fontSize: 10.5, color: E.faint }}>{city}</span>}
        <div style={{ flex: 1, height: 1, background: E.line }} />
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${name}`}
          style={{ width: 26, height: 26, borderRadius: 8, border: `1px solid ${E.line}`, background: E.panel, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={14} color={accent} />
        </button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SPEC CARD — the seller's colour is the only accent
  ═══════════════════════════════════ */
  const specCard = (item: any) => {
    const partner = brandMap.get(item.brandId);
    const accent = brandAccentColor(partner);
    const q = getQty(item.listingId);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;
    const specs = specsOf(item).slice(0, 3);
    const colours = savedColorsOf(item);
    const configurable = hasVariants(item);
    const isWished = wished.has(item.listingId);
    const low = (item.stock ?? 0) > 0 && (item.stock ?? 0) <= 5;

    return (
      <div key={item.listingId} className="es-card" onClick={() => openDetail(item)}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = accent;
          el.style.boxShadow = `0 0 0 1px ${accent}33, 0 18px 40px rgba(27,14,6,.14)`;
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = E.line;
          el.style.boxShadow = "none";
        }}>
        <div className="es-shot">
          {item.photos?.[0]
            ? <img src={item.photos[0]} alt={item.name}
                onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.src = appLogo; el.style.opacity = "0.25"; }} />
            : <Package size={34} color={E.faint} />}

          {discount && (
            <span className="es-tag es-mono" style={{ left: 9, background: accent, color: "#fff" }}>−{discount}%</span>
          )}
          {low && (
            <span className="es-tag es-mono" style={{ left: 9, top: discount ? 32 : 9, background: E.panel2, color: E.warn, border: `1px solid ${E.line}` }}>
              {item.stock} left
            </span>
          )}
          <button onClick={(e) => toggleWish(item.listingId, e)} aria-label="Save"
            style={{ position: "absolute", top: 8, right: 8, width: 27, height: 27, borderRadius: 8, background: "rgba(255,255,255,.9)", border: `1px solid ${E.line}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Heart size={12} color={isWished ? accent : E.muted} fill={isWished ? accent : "none"} />
          </button>

          {colours.length > 0 && (
            <div style={{ position: "absolute", bottom: 8, left: 9, display: "flex", gap: 4 }}>
              {colours.slice(0, 4).map((c) => (
                <span key={c.id} title={c.name}
                  style={{ width: 11, height: 11, borderRadius: "50%", background: c.hex || E.raise, border: `1px solid rgba(27,14,6,.15)` }} />
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
          <div className="es-mono" style={{ fontSize: 9.5, letterSpacing: ".08em", color: E.faint, textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {catPathLabel(item.categoryId) || item.category || "device"}
          </div>

          <div style={{ fontSize: 13.5, fontWeight: 600, color: E.ink, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: 35 }}>
            {item.name}
          </div>

          {specs.length > 0 && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {specs.map((s) => (
                <span key={s.name} className="es-spec es-mono">
                  {s.name}<b>{s.value}</b>
                </span>
              ))}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: "auto", paddingTop: 4 }}>
            <span className="es-mono es-disp" style={{ fontSize: 17, color: E.ink }}>{price(item.rate ?? 0)}</span>
            {discount && (
              <span className="es-mono" style={{ fontSize: 11, color: E.faint, textDecoration: "line-through" }}>
                {price(item.compareAtPrice ?? 0)}
              </span>
            )}
            {hasRating && (
              <span className="es-mono" style={{ marginLeft: "auto", fontSize: 10.5, color: E.muted, display: "flex", alignItems: "center", gap: 3 }}>
                <Star size={10} fill={E.warn} color={E.warn} />{summary.avgRating.toFixed(1)}
              </span>
            )}
          </div>

          {q > 0 && !configurable ? (
            <div className="es-step" onClick={(e) => e.stopPropagation()} style={{ borderColor: accent }}>
              <button aria-label="Remove one" style={{ background: accent }}
                onClick={(e) => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                <Minus size={13} color="#fff" />
              </button>
              <span className="es-mono" style={{ fontSize: 13, fontWeight: 700, color: accent }}>{q}</span>
              <button aria-label="Add one" style={{ background: accent }} onClick={(e) => handleAddToCart(item, e)}>
                <Plus size={13} color="#fff" />
              </button>
            </div>
          ) : (
            <button className="es-add" onClick={(e) => handleAddToCart(item, e)}
              style={{ borderColor: accent, color: accent }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = accent; (e.currentTarget as HTMLElement).style.color = "#fff"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = accent; }}>
              {configurable ? <Sliders size={13} /> : <Zap size={13} />}
              {configurable ? "CONFIGURE" : "ADD TO CART"}
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     RESULTS
  ═══════════════════════════════════ */
  const emptyState = (title: string, body: string) => (
    <div style={{ textAlign: "center", padding: "64px 20px" }}>
      <div style={{ width: 54, height: 54, borderRadius: 14, background: E.panel, border: `1px solid ${E.line}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <Cpu size={22} color={E.faint} />
      </div>
      <h3 className="es-disp" style={{ fontSize: 18, color: E.ink, marginBottom: 8 }}>{title}</h3>
      <p style={{ color: E.muted, fontSize: 13, marginBottom: 22 }}>{body}</p>
      <button onClick={resetAllFilters} className="es-hbtn" style={{ margin: "0 auto", padding: "10px 20px" }}>
        Clear filters
      </button>
    </div>
  );

  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <div className="es-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: E.faint, textTransform: "uppercase" }}>
          Results
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={activeSort} onChange={(e) => setActiveSort(e.target.value as SortKey)} aria-label="Sort"
            className="es-mono"
            style={{ background: E.panel, border: `1px solid ${E.line}`, color: E.inkSoft, borderRadius: 9, padding: "6px 10px", fontSize: 11, cursor: "pointer", outline: "none" }}>
            <option value="default">Featured</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price ↑</option>
            <option value="price_desc">Price ↓</option>
            <option value="name_asc">Name A–Z</option>
          </select>
          {locationActive && (
            <button onClick={handleToggleGlobal} className="es-hbtn">
              {showGlobal ? <MapPin size={12} /> : <Globe size={12} />}
              {showGlobal ? "Local" : "Global"}
            </button>
          )}
        </div>
      </div>

      {productsByBrand.length === 0 ? (
        filteredProducts.length === 0
          ? emptyState(
              activeFilterName ? `No ${activeFilterName} in stock` : `Nothing in ${cat?.name ?? "this category"} yet`,
              "Press another key above, or clear the filters."
            )
          : locationDenied
            ? emptyState("Location is off", "Turn it on to see sellers who can ship to you.")
            : emptyState(
                showGlobal ? "No global sellers here" : "No local sellers here",
                `Switch to ${showGlobal ? "local" : "global"} above, or clear the filters.`
              )
      ) : (
        productsByBrand.map((group) => (
          <div key={group.brandId}>
            {sellerRow(group.brandId)}
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => specCard(item))}
            </div>
          </div>
        ))
      )}
    </div>
  );

  /* ═══════════════════════════════════
     DETAIL — full spec sheet
  ═══════════════════════════════════ */
  const detailModal = () => {
    if (!detailItem) return null;
    const item = detailItem;
    const partner = brandMap.get(item.brandId);
    const accent = brandAccentColor(partner);
    const brandName = partner?.name;
    const img = item.photos?.[0];
    const stock = item.stock ?? 0;
    const soldOut = isOutOfStock(item);
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;

    const variantDefs = variantAttrsForItem(item);
    const specs = specsOf(item);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;

    const allChosen = variantDefs.every((a: any) => selectedVariants[String(a.id)] != null);
    const canContinue = !soldOut && qty > 0 && qty <= stock && allChosen;

    const content = (
      <div style={{ background: E.voidSoft, border: `1px solid ${E.line}`, borderRadius: isMobile ? "16px 16px 0 0" : 16, overflow: "hidden", width: "100%", maxWidth: isMobile ? "100%" : 480, maxHeight: isMobile ? "88vh" : "86vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 2, background: accent, flexShrink: 0 }} />
        {isMobile && <div style={{ width: 34, height: 3, borderRadius: 2, background: E.raise, margin: "10px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "16px 18px 0" }}>
          <div style={{ position: "relative", width: "100%", aspectRatio: "4/3", borderRadius: 12, background: `radial-gradient(120% 90% at 50% 15%, ${E.panel2} 0%, ${E.panel} 70%)`, border: `1px solid ${E.line}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14, overflow: "hidden" }}>
            {img
              ? <img src={img} alt={item.name} style={{ width: "80%", height: "80%", objectFit: "contain", filter: "drop-shadow(0 14px 22px rgba(27,14,6,.18))" }} />
              : <Package size={40} color={E.faint} />}
            <button onClick={closeDetail} aria-label="Close"
              style={{ position: "absolute", top: 10, right: 10, width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,.9)", border: `1px solid ${E.line}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <X size={14} color={E.inkSoft} />
            </button>
            {discount && (
              <span className="es-tag es-mono" style={{ left: 10, background: accent, color: "#fff" }}>−{discount}%</span>
            )}
          </div>

          {brandName && (
            <p className="es-mono" style={{ fontSize: 10, letterSpacing: ".16em", textTransform: "uppercase", color: accent, marginBottom: 5 }}>{brandName}</p>
          )}
          <div className="es-disp" style={{ fontSize: 17, color: E.ink, marginBottom: 5 }}>{item.name}</div>
          {item.categoryId && (
            <p className="es-mono" style={{ fontSize: 10.5, color: E.faint, marginBottom: 12 }}>{catPathLabel(item.categoryId)}</p>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <span className="es-mono es-disp" style={{ fontSize: 22, color: E.ink }}>{price(item.rate ?? 0)}</span>
            {discount && <span className="es-mono" style={{ fontSize: 12, color: E.faint, textDecoration: "line-through" }}>{price(item.compareAtPrice ?? 0)}</span>}
            {hasRating && (
              <span className="es-mono" style={{ fontSize: 11, color: E.muted, display: "flex", alignItems: "center", gap: 4 }}>
                <Star size={11} fill={E.warn} color={E.warn} />
                {summary.avgRating.toFixed(1)} ({summary.totalReviews.toLocaleString()})
              </span>
            )}
          </div>

          {/* ── configurable options ── */}
          {variantDefs.map((attr: any) => {
            const key = String(attr.id);
            const chosen = selectedVariants[key];
            const isColour = attr.inputType === "color";
            return (
              <div key={key} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 9 }}>
                  <span className="es-mono" style={{ fontSize: 10, letterSpacing: ".18em", color: E.muted, textTransform: "uppercase" }}>{attr.name}</span>
                  {chosen == null && <span style={{ fontSize: 10, fontWeight: 600, color: E.warn }}>pick one</span>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {attr.options.map((opt: any) => {
                    const on = String(chosen) === String(opt.id);
                    return (
                      <button key={opt.id} onClick={() => setSelectedVariants((p) => ({ ...p, [key]: opt.id }))}
                        style={{
                          display: "flex", alignItems: "center", gap: 7, padding: isColour ? "6px 12px 6px 7px" : "8px 13px",
                          borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 600,
                          border: `1px solid ${on ? accent : E.line}`,
                          background: on ? E.raise : E.panel, color: on ? E.ink : E.inkSoft,
                        }}>
                        {isColour && (
                          <span style={{ width: 16, height: 16, borderRadius: "50%", background: opt.hex || E.raise, border: "1px solid rgba(27,14,6,.15)" }} />
                        )}
                        {opt.name || opt.standard || String(opt.id)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* ── the spec sheet ── */}
          {specs.length > 0 && (
            <div style={{ border: `1px solid ${E.line}`, borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
              <div className="es-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: E.muted, textTransform: "uppercase", padding: "10px 13px", borderBottom: `1px solid ${E.line}`, background: E.panel }}>
                Specifications
              </div>
              {specs.map((s, i) => (
                <div key={s.name} style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "9px 13px", background: i % 2 ? E.panel : E.voidSoft, fontSize: 12 }}>
                  <span style={{ color: E.muted }}>{s.name}</span>
                  <span className="es-mono" style={{ color: E.ink, textAlign: "right", fontWeight: 500 }}>{s.value}</span>
                </div>
              ))}
            </div>
          )}

          {item.vendor && (
            <p style={{ fontSize: 11.5, color: E.muted, marginBottom: 10 }}>
              <span style={{ color: E.inkSoft, fontWeight: 600 }}>Vendor:</span> {item.vendor}
            </p>
          )}
          {item.tags?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {item.tags.map((t: string) => (
                <span key={t} className="es-mono" style={{ fontSize: 10, color: E.muted, background: E.panel, border: `1px solid ${E.line}`, borderRadius: 6, padding: "3px 8px" }}>#{t}</span>
              ))}
            </div>
          )}

          {item.description && (
            <div style={{ fontSize: 13, color: E.inkSoft, lineHeight: 1.7, marginBottom: 16 }}
              dangerouslySetInnerHTML={{ __html: item.description }} />
          )}

          {soldOut ? (
            <p className="es-mono" style={{ fontSize: 11, color: E.faint, marginBottom: 12 }}>OUT OF STOCK</p>
          ) : stock <= 5 ? (
            <p className="es-mono" style={{ fontSize: 11, color: E.warn, marginBottom: 12 }}>ONLY {stock} LEFT</p>
          ) : null}

          {!soldOut && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <span className="es-mono" style={{ fontSize: 10, letterSpacing: ".18em", color: E.muted }}>QTY</span>
              <div style={{ display: "flex", border: `1px solid ${E.line}`, borderRadius: 10, overflow: "hidden" }}>
                <button onClick={() => setQty((v) => Math.max(1, v - 1))} aria-label="Decrease"
                  style={{ width: 38, height: 38, border: "none", background: E.panel, cursor: "pointer", color: E.inkSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={13} />
                </button>
                <div className="es-mono" style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: E.ink, background: E.voidSoft }}>{qty}</div>
                <button onClick={() => setQty((v) => Math.min(stock || 1, v + 1))} disabled={qty >= stock} aria-label="Increase"
                  style={{ width: 38, height: 38, border: "none", background: E.panel, cursor: qty >= stock ? "not-allowed" : "pointer", color: E.inkSoft, opacity: qty >= stock ? .4 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={13} />
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "13px 18px 18px", display: "flex", gap: 9, flexShrink: 0, borderTop: `1px solid ${E.line}` }}>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(false)}
            style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: `1px solid ${canContinue ? accent : E.line}`, background: "transparent", color: canContinue ? accent : E.faint, fontWeight: 700, fontSize: 12, letterSpacing: ".04em", cursor: canContinue ? "pointer" : "not-allowed" }}>
            ADD TO CART
          </button>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(true)}
            style={{ flex: 1.15, padding: "12px 0", borderRadius: 10, border: "none", background: canContinue ? accent : E.raise, color: canContinue ? "#fff" : E.faint, fontWeight: 700, fontSize: 12, letterSpacing: ".04em", cursor: canContinue ? "pointer" : "not-allowed" }}>
            BUY NOW
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeDetail}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.5)", backdropFilter: "blur(5px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>
              {content}
            </motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: .96, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: .96, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 480, maxWidth: "100%" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  const locationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <motion.div initial={{ scale: .94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .94, y: 20 }} onClick={(e) => e.stopPropagation()}
            className="es-panel" style={{ width: 320, padding: 22, textAlign: "center" }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: E.panel2, border: `1px solid ${E.line}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
              <MapPin size={19} color={ATLAS_RED} />
            </div>
            <h3 className="es-disp" style={{ fontSize: 16, color: E.ink, marginBottom: 8 }}>Location is off</h3>
            <p style={{ color: E.muted, fontSize: 12.5, lineHeight: 1.6, marginBottom: 18 }}>
              Allow location in your browser settings to see sellers who can ship to you.
            </p>
            <button onClick={() => setLocationModalOpen(false)} className="es-hbtn" style={{ margin: "0 auto", padding: "10px 22px" }}>
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const bottomTabs = () => (isMobile && userId ? (
    <div style={S.tabs}>
      {[
        { icon: <Home size={20} />, label: "Home", on: false, go: () => navigate(`/StoreProductsHome/${userId}`) },
        { icon: <Cpu size={20} />, label: "Tech", on: true, go: () => navigate("/ElectronicsStore") },
        {
          label: "Cart", on: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={20} />
              {cartTotal > 0 && (
                <span className="es-mono" style={{ position: "absolute", top: -5, right: -7, background: ATLAS_RED, color: "#fff", fontSize: 8, fontWeight: 700, minWidth: 15, height: 15, padding: "0 3px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal}</span>
              )}
            </div>
          ), go: goToCheckout,
        },
        { icon: <ShoppingBasket size={20} />, label: "Sellers", on: false, go: () => navigate("/Stores") },
        { icon: <User size={20} />, label: "Profile", on: false, go: () => navigate(`/profile/${userId}`) },
      ].map((t) => (
        <button key={t.label} onClick={t.go} className={`es-tab${t.on ? " on" : ""}`}>
          {React.cloneElement(t.icon as any, { color: t.on ? ATLAS_RED : E.faint })}
          <span>{t.label}</span>
        </button>
      ))}
    </div>
  ) : null);

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div className="es">
      <style>{GLOBAL_CSS}</style>

      <header className="es-rail">
        <div className="es-scan"><i /></div>
        {railRow()}
        <div style={{ padding: isMobile ? "0 14px" : "0 22px" }}>{!isMobile && keyRail()}</div>
      </header>

      {mobileBrowseSheet()}

      <div style={{ maxWidth: 1320, margin: "0 auto", padding: isMobile ? "14px 14px 0" : "22px 22px 0" }}>
        {isMobile && <div style={{ marginBottom: 6 }}>{keyRail()}</div>}
        {heading()}

        <div style={{ display: "grid", gridTemplateColumns: !isMobile && desktopCatOpen ? "252px 1fr" : "1fr", gap: 20, alignItems: "start" }}>
          {!isMobile && desktopCatOpen && (
            <aside style={{ position: "sticky", top: 128, maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
              {browsePanel()}
            </aside>
          )}
          <main style={{ minWidth: 0 }}>
            {subTiles()}
            {renderProducts(isMobile)}
          </main>
        </div>
      </div>

      {detailModal()}
      {locationModal()}
    
    </div>
  );
};

export default ElectronicsStore;